import { Alert, Box, CircularProgress } from '@mui/material';
import React from 'react';
import ChatNotificationChannelLabel from '../../../components/admin/chatNotificationChannels/ChatNotificationChannelLabel';
import { ChatNotificationChannel } from '../../../components/admin/chatNotificationChannels/ChatNotificationChannelIcon';
import { Palette } from 'Palette';
import { StyledButton } from '../../../components/styles/StyledButtons';
import ChatTimingControls from './ChatTimingControls';

interface ChatChannelCardProps {
  channel: ChatNotificationChannel;
  label: string;
  connected: boolean;
  connectedDetail: string | null;
  timing: 'immediate' | 'unanswered';
  unansweredMinutes: number;
  saving: boolean;
  disconnecting: boolean;
  connecting?: boolean;
  warningMessage?: string;
  authorizeLabel?: string;
  onRemove: () => void;
  onAuthorize?: () => void;
  onTimingChange: (timing: 'immediate' | 'unanswered') => void;
  onMinutesChange: (minutes: number) => void;
}

const ChatChannelCard: React.FC<ChatChannelCardProps> = ({
  channel,
  label,
  connected,
  connectedDetail,
  timing,
  unansweredMinutes,
  saving,
  disconnecting,
  connecting = false,
  warningMessage,
  authorizeLabel,
  onRemove,
  onAuthorize,
  onTimingChange,
  onMinutesChange,
}) => (
  <Box sx={{ px: 2, py: 2, borderBottom: `1px solid ${Palette.tone1.border}` }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: onAuthorize ? 'flex-start' : 'center',
        mb: 1.5,
      }}
    >
      <ChatNotificationChannelLabel
        channel={channel}
        label={label}
        connectedDetail={connectedDetail}
      />
      <StyledButton
        onClick={onRemove}
        disabled={disconnecting || saving}
        sx={{ minWidth: '5rem', flexShrink: 0 }}
      >
        {disconnecting ? <CircularProgress size={18} /> : 'Remove'}
      </StyledButton>
    </Box>
    {onAuthorize && !connected && (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="warning" sx={{ mb: 1 }}>
          {warningMessage}
        </Alert>
        <StyledButton
          onClick={onAuthorize}
          disabled={connecting || saving}
          sx={{
            backgroundColor: Palette.default.buttonBg,
            color: Palette.default.buttonText,
            '&:hover': {
              backgroundColor: Palette.tone1.buttonBg,
              color: Palette.tone1.buttonText,
            },
          }}
        >
          {connecting ? <CircularProgress size={20} /> : authorizeLabel}
        </StyledButton>
      </Box>
    )}
    <ChatTimingControls
      timing={timing}
      unansweredMinutes={unansweredMinutes}
      disabled={saving}
      onTimingChange={onTimingChange}
      onMinutesChange={onMinutesChange}
    />
  </Box>
);

export default ChatChannelCard;
